import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { TranslatedText } from '@/components/translated-text'
import { useTranslation } from '@/hooks/useTranslation'
import { 
  Target, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle, 
  Lightbulb, 
  Zap, 
  DollarSign,
  Package,
  BarChart3,
  Activity,
  RefreshCw,
  Download,
  Settings,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react'

interface InventoryOptimizationProps {
  products: any[]
  movements: any[]
  locations: any[]
  alerts: any[]
  analytics?: any
  kpis?: any
  onApplyRecommendation?: (recommendation: any) => void
  onRefresh?: () => void
}

interface OptimizationRecommendation {
  id: string
  type: 'stock_adjustment' | 'reorder_point' | 'location_transfer' | 'pricing' | 'discontinuation'
  priority: 'high' | 'medium' | 'low'
  title: string
  description: string
  impact: 'cost_savings' | 'revenue_increase' | 'efficiency' | 'risk_reduction'
  potentialSavings?: number
  potentialRevenue?: number
  confidence: number
  products: string[]
  locations?: string[]
  action: string
  estimatedEffort: 'low' | 'medium' | 'high'
}

interface OptimizationMetrics {
  overallScore: number
  costOptimization: number
  spaceUtilization: number
  turnoverRate: number
  stockAccuracy: number
  fillRate: number
}

export function InventoryOptimization({ 
  products, 
  movements, 
  locations, 
  alerts, 
  kpis,
  onApplyRecommendation,
  onRefresh 
}: InventoryOptimizationProps) {
  const { tSync } = useTranslation()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedPriority, setSelectedPriority] = useState('all')
  const [appliedRecommendations, setAppliedRecommendations] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  // Calculate real stock accuracy based on movements vs actual stock
  const stockAccuracy = useMemo(() => {
    if (movements.length === 0) return 100
    
    // Calculate expected stock based on movements
    const productStockCalculations = products.map(product => {
      const productMovements = movements.filter(m => m.productId === product.id)
      const expectedStock = productMovements.reduce((sum, movement) => {
        const quantity = Number(movement.quantity || 0)
        if (['INBOUND', 'PURCHASE', 'TRANSFER_IN', 'ADJUSTMENT_IN', 'RETURN_IN'].includes(movement.movementType)) {
          return sum + quantity
        } else if (['OUTBOUND', 'SALE', 'TRANSFER_OUT', 'ADJUSTMENT_OUT', 'RETURN_OUT', 'DAMAGE', 'THEFT'].includes(movement.movementType)) {
          return sum - quantity
        }
        return sum
      }, 0)
      
      const actualStock = Number(product.stockQuantity || 0)
      const variance = Math.abs(expectedStock - actualStock)
      return { variance, actualStock }
    })
    
    const totalVariance = productStockCalculations.reduce((sum, calc) => sum + calc.variance, 0)
    const totalStock = productStockCalculations.reduce((sum, calc) => sum + calc.actualStock, 0)
    
    return totalStock > 0 ? Math.max(0, 100 - (totalVariance / totalStock) * 100) : 100
  }, [products, movements])
  
  // Calculate real fill rate based on out-of-stock vs total products
  const fillRate = useMemo(() => {
    const outOfStockCount = products.filter(product => 
      Number(product.stockQuantity || 0) === 0
    ).length
    const totalProducts = products.length
    return totalProducts > 0 ? Math.max(0, 100 - (outOfStockCount / totalProducts) * 100) : 100
  }, [products])

  // Calculate optimization metrics
  const metrics = useMemo((): OptimizationMetrics => {
    const totalProducts = products.length
    
    const lowStockItems = products.filter(product => {
      const stock = typeof product.stockQuantity === 'string' ? parseFloat(product.stockQuantity) : product.stockQuantity
      const reorderPoint = typeof product.reorderPoint === 'string' ? parseFloat(product.reorderPoint) : product.reorderPoint || 10
      return stock <= reorderPoint
    }).length

    const outOfStockItems = products.filter(product => {
      const stock = typeof product.stockQuantity === 'string' ? parseFloat(product.stockQuantity) : product.stockQuantity
      return stock === 0
    }).length

    const avgTurnover = kpis?.inventoryTurnover || 6

    // Calculate optimization scores
    const costOptimization = Math.max(0, 100 - (lowStockItems / totalProducts) * 100)
    const spaceUtilization = Math.max(0, 100 - (outOfStockItems / totalProducts) * 100)
    const turnoverRate = Math.min(100, (avgTurnover / 12) * 100)
    
    const overallScore = (costOptimization + spaceUtilization + turnoverRate + stockAccuracy + fillRate) / 5

    return {
      overallScore: Math.round(overallScore),
      costOptimization: Math.round(costOptimization),
      spaceUtilization: Math.round(spaceUtilization),
      turnoverRate: Math.round(turnoverRate),
      stockAccuracy: Math.round(stockAccuracy),
      fillRate: Math.round(fillRate)
    }
  }, [products, kpis, stockAccuracy, fillRate])

  // Generate optimization recommendations
  const recommendations = useMemo((): OptimizationRecommendation[] => {
    const recs: OptimizationRecommendation[] = []

    // Analyze products for recommendations
    products.forEach(product => {
      const stock = typeof product.stockQuantity === 'string' ? parseFloat(product.stockQuantity) : product.stockQuantity
      const reorderPoint = typeof product.reorderPoint === 'string' ? parseFloat(product.reorderPoint) : product.reorderPoint || 10
      const price = typeof product.unitPrice === 'string' ? parseFloat(product.unitPrice) : product.unitPrice || 0
      const cost = typeof product.costPrice === 'string' ? parseFloat(product.costPrice) : product.costPrice || 0

      // Low stock recommendation
      if (stock <= reorderPoint && stock > 0) {
        recs.push({
          id: `low-stock-${product.id}`,
          type: 'reorder_point',
          priority: stock <= reorderPoint * 0.5 ? 'high' : 'medium',
          title: `${tSync('Reorder')} ${product.name}`,
          description: `${tSync('Stock level')} (${stock}) ${tSync('is below reorder point')} (${reorderPoint}). ${tSync('Consider placing a purchase order')}.`,
          impact: 'risk_reduction',
          potentialSavings: stock * cost * 0.1, // 10% of stock value
          confidence: 95,
          products: [product.id],
          action: `${tSync('Increase stock to')} ${reorderPoint * 2} ${tSync('units')}`,
          estimatedEffort: 'low'
        })
      }

      // Overstock recommendation
      if (stock > reorderPoint * 3) {
        recs.push({
          id: `overstock-${product.id}`,
          type: 'stock_adjustment',
          priority: 'medium',
          title: `${tSync('Reduce Overstock for')} ${product.name}`,
          description: `${tSync('High stock level')} (${stock}) ${tSync('may indicate overstocking. Consider reducing inventory')}`,
          impact: 'cost_savings',
          potentialSavings: (stock - reorderPoint * 2) * cost * 0.2,
          confidence: 80,
          products: [product.id],
          action: `${tSync('Reduce stock to')} ${reorderPoint * 2} ${tSync('units')}`,
          estimatedEffort: 'medium'
        })
      }

      // Pricing optimization
      if (price > cost * 2.5 && cost > 0) {
        const markupPercentage = ((price - cost) / cost * 100).toFixed(1)
        recs.push({
          id: `pricing-${product.id}`,
          type: 'pricing',
          priority: 'low',
          title: `${tSync('Optimize Pricing for')} ${product.name}`,
          description: `${tSync('High markup')} (${markupPercentage}%) ${tSync('may impact sales volume')}.`,
          impact: 'revenue_increase',
          potentialRevenue: stock * price * 0.05, // 5% increase
          confidence: 70,
          products: [product.id],
          action: tSync('Consider reducing price by 5-10%'),
          estimatedEffort: 'low'
        })
      } else if (cost === 0 && price > 0) {
        // Handle products with zero cost price
        recs.push({
          id: `pricing-${product.id}`,
          type: 'pricing',
          priority: 'medium',
          title: `${tSync('Review Pricing for')} ${product.name}`,
          description: tSync('Product has zero cost price but positive selling price. Review cost structure.'),
          impact: 'efficiency',
          potentialSavings: 0,
          confidence: 85,
          products: [product.id],
          action: tSync('Update cost price or review pricing strategy'),
          estimatedEffort: 'low'
        })
      }

      // Discontinuation recommendation for slow movers (based on real data)
      const productMovements = movements.filter(m => m.productId === product.id)
      const last90Days = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
      const recentMovements = productMovements.filter(m => 
        new Date(m.movementDate) >= last90Days
      )
      
      // Calculate movement rate (outgoing movements per month)
      const outgoingMovements = recentMovements.filter(m => 
        ['OUTBOUND', 'SALE', 'TRANSFER_OUT', 'ADJUSTMENT_OUT', 'RETURN_OUT', 'DAMAGE', 'THEFT'].includes(m.movementType)
      ).length
      
      const movementRate = outgoingMovements / 3 // 3 months
      
      // Recommend discontinuation if movement rate is very low (less than 1 movement per month)
      if (stock > 0 && movementRate < 1 && recentMovements.length > 0) {
        recs.push({
          id: `discontinue-${product.id}`,
          type: 'discontinuation',
          priority: 'low',
          title: `${tSync('Consider Discontinuing')} ${product.name}`,
          description: `${tSync('Low movement rate')} (${movementRate.toFixed(1)} ${tSync('movements/month')}) ${tSync('suggests this product may not be profitable')}.`,
          impact: 'cost_savings',
          potentialSavings: stock * cost,
          confidence: 60,
          products: [product.id],
          action: tSync('Phase out product or run clearance sale'),
          estimatedEffort: 'high'
        })
      }
    })

    // Location optimization recommendations
    if (locations.length > 1) {
      recs.push({
        id: 'location-balance',
        type: 'location_transfer',
        priority: 'medium',
        title: tSync('Balance Stock Across Locations'),
        description: `${tSync('Optimize inventory distribution across')} ${locations.length} ${tSync('locations for better efficiency')}.`,
        impact: 'efficiency',
        potentialSavings: 5000,
        confidence: 85,
        products: products.slice(0, 5).map(p => p.id),
        locations: locations.map(l => l.id),
        action: tSync('Transfer stock between locations based on demand patterns'),
        estimatedEffort: 'medium'
      })
    }

    return recs.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 }
      return priorityOrder[b.priority] - priorityOrder[a.priority]
    })
  }, [products, locations, movements, tSync])

  const filteredRecommendations = recommendations.filter(rec => {
    const categoryMatch = selectedCategory === 'all' || 
      products.find(p => p.id === rec.products[0])?.category === selectedCategory
    const priorityMatch = selectedPriority === 'all' || rec.priority === selectedPriority
    return categoryMatch && priorityMatch
  })

  const handleApplyRecommendation = (recommendation: OptimizationRecommendation) => {
    setAppliedRecommendations(prev => new Set([...prev, recommendation.id]))
    
    toast({
      title: tSync("Recommendation Applied"),
      description: recommendation.title,
    })

    if (onApplyRecommendation) {
      onApplyRecommendation(recommendation)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200'
      case 'medium': return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'low': return 'bg-green-100 text-green-800 border-green-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getImpactIcon = (impact: string) => {
    switch (impact) {
      case 'cost_savings': return <TrendingDown className="w-4 h-4 text-green-600" />
      case 'revenue_increase': return <TrendingUp className="w-4 h-4 text-blue-600" />
      case 'efficiency': return <Zap className="w-4 h-4 text-purple-600" />
      case 'risk_reduction': return <AlertTriangle className="w-4 h-4 text-amber-600" />
      default: return <Target className="w-4 h-4 text-gray-600" />
    }
  }

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'low': return 'text-green-600'
      case 'medium': return 'text-amber-600'
      case 'high': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  const translatePriority = (priority: string) => {
    switch (priority) {
      case 'high': return tSync('High')
      case 'medium': return tSync('Medium')
      case 'low': return tSync('Low')
      default: return priority
    }
  }

  const translateEffort = (effort: string) => {
    switch (effort) {
      case 'low': return tSync('Low')
      case 'medium': return tSync('Medium')
      case 'high': return tSync('High')
      default: return effort
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold"><TranslatedText text="Inventory Optimization" /></h2>
          <p className="text-muted-foreground"><TranslatedText text="AI-powered recommendations to optimize your inventory performance" /></p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all"><TranslatedText text="All Categories" /></SelectItem>
              <SelectItem value="electronics"><TranslatedText text="Electronics" /></SelectItem>
              <SelectItem value="clothing"><TranslatedText text="Clothing" /></SelectItem>
              <SelectItem value="books"><TranslatedText text="Books" /></SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedPriority} onValueChange={setSelectedPriority}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all"><TranslatedText text="All Priority" /></SelectItem>
              <SelectItem value="high"><TranslatedText text="High" /></SelectItem>
              <SelectItem value="medium"><TranslatedText text="Medium" /></SelectItem>
              <SelectItem value="low"><TranslatedText text="Low" /></SelectItem>
            </SelectContent>
          </Select>
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              <TranslatedText text="Refresh" />
            </Button>
          )}
        </div>
      </div>

      {/* Optimization Score */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Overall Score" /></p>
                <p className="text-xl font-bold">{metrics.overallScore}%</p>
              </div>
            </div>
            <Progress value={metrics.overallScore} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Cost Optimization" /></p>
                <p className="text-xl font-bold">{metrics.costOptimization}%</p>
              </div>
            </div>
            <Progress value={metrics.costOptimization} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Space Utilization" /></p>
                <p className="text-xl font-bold">{metrics.spaceUtilization}%</p>
              </div>
            </div>
            <Progress value={metrics.spaceUtilization} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Turnover Rate" /></p>
                <p className="text-xl font-bold">{metrics.turnoverRate}%</p>
              </div>
            </div>
            <Progress value={metrics.turnoverRate} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Stock Accuracy" /></p>
                <p className="text-xl font-bold">{metrics.stockAccuracy}%</p>
              </div>
            </div>
            <Progress value={metrics.stockAccuracy} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Fill Rate" /></p>
                <p className="text-xl font-bold">{metrics.fillRate}%</p>
              </div>
            </div>
            <Progress value={metrics.fillRate} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      <Tabs defaultValue="recommendations" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="recommendations"><TranslatedText text="Recommendations" /></TabsTrigger>
          <TabsTrigger value="insights"><TranslatedText text="Insights" /></TabsTrigger>
          <TabsTrigger value="actions"><TranslatedText text="Action Plan" /></TabsTrigger>
        </TabsList>

        <TabsContent value="recommendations" className="space-y-4">
          <div className="space-y-4">
            {filteredRecommendations.map((recommendation) => (
              <Card key={recommendation.id} className={appliedRecommendations.has(recommendation.id) ? 'opacity-50' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{recommendation.title}</h3>
                        <Badge className={getPriorityColor(recommendation.priority)}>
                          {translatePriority(recommendation.priority)}
                        </Badge>
                        {getImpactIcon(recommendation.impact)}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-3">
                        {recommendation.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground"><TranslatedText text="Confidence" />:</span>
                          <span className="font-medium">{recommendation.confidence}%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground"><TranslatedText text="Effort" />:</span>
                          <span className={`font-medium ${getEffortColor(recommendation.estimatedEffort)}`}>
                            {translateEffort(recommendation.estimatedEffort)}
                          </span>
                        </div>
                        {recommendation.potentialSavings && (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground"><TranslatedText text="Savings" />:</span>
                            <span className="font-medium text-green-600">
                              {recommendation.potentialSavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw
                            </span>
                          </div>
                        )}
                        {recommendation.potentialRevenue && (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground"><TranslatedText text="Revenue" />:</span>
                            <span className="font-medium text-blue-600">
                              {recommendation.potentialRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3 p-2 bg-muted rounded-lg">
                        <p className="text-sm font-medium"><TranslatedText text="Action" />:</p>
                        <p className="text-sm text-muted-foreground">{recommendation.action}</p>
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      {appliedRecommendations.has(recommendation.id) ? (
                        <Button variant="outline" disabled>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          <TranslatedText text="Applied" />
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleApplyRecommendation(recommendation)}
                          size="sm"
                        >
                          <TranslatedText text="Apply" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {filteredRecommendations.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2"><TranslatedText text="No Recommendations" /></h3>
                  <p className="text-muted-foreground">
                    <TranslatedText text="Your inventory is well optimized! No immediate recommendations at this time." />
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="insights" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  <TranslatedText text="Key Insights" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900"><TranslatedText text="High Turnover Products" /></p>
                    <p className="text-sm text-blue-700">
                      {products.filter(p => {
                        const productMovements = movements.filter(m => m.productId === p.id)
                        const last30Days = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                        const recentMovements = productMovements.filter(m => 
                          new Date(m.movementDate) >= last30Days
                        )
                        const outgoingMovements = recentMovements.filter(m => 
                          ['OUTBOUND', 'SALE', 'TRANSFER_OUT', 'ADJUSTMENT_OUT', 'RETURN_OUT', 'DAMAGE', 'THEFT'].includes(m.movementType)
                        ).length
                        return outgoingMovements >= 3 // 3+ movements in last 30 days
                      }).length} <TranslatedText text="products showing excellent movement" />
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900"><TranslatedText text="Attention Needed" /></p>
                    <p className="text-sm text-amber-700">
                      {alerts.length} <TranslatedText text="items require immediate attention" />
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <Package className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-900"><TranslatedText text="Space Optimization" /></p>
                    <p className="text-sm text-green-700">
                      <TranslatedText text="Potential to free up" /> {Math.floor(products.length * 0.1)} <TranslatedText text="storage locations" />
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  <TranslatedText text="Performance Trends" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium"><TranslatedText text="Inventory Turnover" /></span>
                  <div className="flex items-center gap-1">
                    <ArrowUpRight className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600">+12%</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium"><TranslatedText text="Stock Accuracy" /></span>
                  <div className="flex items-center gap-1">
                    <ArrowUpRight className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600">+2.1%</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium"><TranslatedText text="Carrying Costs" /></span>
                  <div className="flex items-center gap-1">
                    <ArrowDownRight className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-600">-5.3%</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium"><TranslatedText text="Fill Rate" /></span>
                  <div className="flex items-center gap-1">
                    <Minus className="w-4 h-4 text-gray-600" />
                    <span className="text-sm text-gray-600">0.0%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="actions" className="space-y-4">
          <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                <TranslatedText text="Recommended Action Plan" />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <span className="text-sm font-bold text-red-600">1</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium"><TranslatedText text="Address High Priority Items" /></p>
                    <p className="text-sm text-muted-foreground">
                      {filteredRecommendations.filter(r => r.priority === 'high').length} <TranslatedText text="high priority recommendations" />
                    </p>
                  </div>
                  <Button size="sm" variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    <TranslatedText text="Export List" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <span className="text-sm font-bold text-amber-600">2</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium"><TranslatedText text="Review Medium Priority Items" /></p>
                    <p className="text-sm text-muted-foreground">
                      {filteredRecommendations.filter(r => r.priority === 'medium').length} <TranslatedText text="medium priority recommendations" />
                    </p>
                  </div>
                  <Button size="sm" variant="outline">
                    <TranslatedText text="Schedule Review" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-sm font-bold text-green-600">3</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium"><TranslatedText text="Monitor Performance" /></p>
                    <p className="text-sm text-muted-foreground">
                      <TranslatedText text="Track optimization metrics and adjust strategies" />
                    </p>
                  </div>
                  <Button size="sm" variant="outline">
                    <TranslatedText text="Set Alerts" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default InventoryOptimization
